import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const SECTIONS = [
  {
    title: '1. Who We Are',
    body: ['Smart Connections provides digital business solutions including:'],
    list: [
      'NFC Business Cards',
      'Google Review Standees',
      'TripAdvisor Review Standees',
      'Smart networking tools',
      'Community and membership services',
    ],
    after: 'By using our website or services, you agree to the practices described in this Privacy Policy.',
  },
  {
    title: '2. Information We Collect',
    body: ['We may collect the following types of information:'],
    sub: [
      {
        heading: 'Personal Information',
        body: 'When you contact us, place an order, or sign up for services, we may collect:',
        list: ['Name', 'Email address', 'Phone number', 'Business name', 'Billing and shipping information'],
      },
      {
        heading: 'Technical Information',
        body: 'When you visit our website, we may automatically collect:',
        list: [
          'IP address',
          'Browser type',
          'Device information',
          'Pages visited',
          'Website usage data',
          'Cookies and analytics data',
        ],
      },
    ],
  },
  {
    title: '3. How We Use Your Information',
    body: ['We use collected information to:'],
    list: [
      'Process orders and payments',
      'Deliver products and services',
      'Provide customer support',
      'Improve our website and services',
      'Communicate updates and promotions',
      'Manage memberships and community access',
      'Analyze website performance and customer engagement',
    ],
  },
  {
    title: '4. Cookies & Tracking Technologies',
    body: ['Our website may use cookies and analytics tools to:'],
    list: [
      'Improve website functionality',
      'Understand visitor behavior',
      'Personalize user experience',
      'Measure marketing performance',
    ],
    after: 'You may disable cookies through your browser settings.',
  },
  {
    title: '5. Sharing of Information',
    body: ['Smart Connections does not sell or rent personal information. We may share limited information with trusted third-party providers involved in:'],
    list: [
      'Payment processing',
      'Website hosting',
      'Shipping and logistics',
      'Analytics and marketing services',
    ],
    after: 'These providers are required to protect your information.',
  },
  {
    title: '6. Third-Party Services',
    body: ['Our products and website may integrate with third-party platforms including:'],
    list: ['Google', 'TripAdvisor', 'Social media platforms', 'Payment gateways'],
    after: 'We are not responsible for the privacy practices of third-party services.',
  },
  {
    title: '7. Data Security',
    body: ['We implement reasonable technical and organizational measures to protect your information from:'],
    list: ['Unauthorized access', 'Loss', 'Misuse', 'Disclosure', 'Alteration'],
    after: 'However, no online transmission or storage system can be guaranteed 100% secure.',
  },
  {
    title: '8. Data Retention',
    body: ['We retain personal information only as long as necessary for:'],
    list: ['Business operations', 'Legal obligations', 'Customer support', 'Service improvement'],
  },
  {
    title: '9. Your Rights',
    body: ['Depending on applicable laws, you may have the right to:'],
    list: [
      'Request access to your personal data',
      'Correct inaccurate information',
      'Request deletion of your data',
      'Withdraw marketing consent',
      'Request data portability',
    ],
    after: 'To exercise these rights, please contact us.',
  },
  {
    title: '10. GDPR Compliance',
    body: ['Smart Connections aims to follow GDPR best practices where applicable. We are committed to:'],
    list: [
      'Transparent data handling',
      'Lawful processing',
      'Protecting user privacy',
      'Respecting consent preferences',
    ],
  },
  {
    title: "11. Children's Privacy",
    body: [
      'Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '12. Changes to This Privacy Policy',
    body: [
      'We may update this Privacy Policy periodically. Any updates will be posted on this page with a revised effective date.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="bg-navy text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="uppercase tracking-widest text-yellow-400 text-sm font-semibold mb-3">
            Legal
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/80">Effective Date: May 2026</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-10 space-y-10">
          <p className="text-gray-700 leading-relaxed">
            Welcome to Smart Connections. Your privacy is important to us. This Privacy Policy explains
            how Smart Connections collects, uses, stores, and protects your information when you use our
            website, products, and services.
          </p>

          {SECTIONS.map((sec, idx) => (
            <div key={idx} data-testid={`privacy-section-${idx}`}>
              <h2 className="text-xl sm:text-2xl font-bold text-navy mb-3">{sec.title}</h2>
              {sec.body?.map((p, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-3">
                  {p}
                </p>
              ))}
              {sec.list && (
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-3">
                  {sec.list.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              )}
              {sec.sub &&
                sec.sub.map((s, i) => (
                  <div key={i} className="mb-4 pl-2 border-l-2 border-coral/40">
                    <h3 className="text-lg font-semibold text-navy mb-1 pl-3">{s.heading}</h3>
                    <p className="text-gray-700 leading-relaxed mb-2 pl-3">{s.body}</p>
                    <ul className="list-disc pl-10 space-y-1 text-gray-700">
                      {s.list.map((li, j) => (
                        <li key={j}>{li}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              {sec.after && <p className="text-gray-700 leading-relaxed">{sec.after}</p>}
            </div>
          ))}

          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-navy mb-3">13. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions regarding this Privacy Policy or your personal data, please contact:
            </p>
            <div className="mt-3 text-gray-700 space-y-1">
              <p className="font-semibold text-navy">Smart Connections</p>
              <p>
                <span className="text-gray-500">Web: </span>
                <a href="https://www.smartconnections.club" className="text-coral hover:underline">
                  www.smartconnections.club
                </a>
              </p>
              <p>
                <span className="text-gray-500">Email: </span>
                <a href="mailto:info@smartconnections.club" className="text-coral hover:underline">
                  info@smartconnections.club
                </a>
              </p>
              <p>
                <span className="text-gray-500">Address: </span>Grand Cayman, Cayman Islands
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
